import { getUiLocale } from '../../utils/uiTranslation';
import { uiText } from '../../utils/uiTranslation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import './OffersHub.css';
import { applyOfferPlaceholders, normalizeOfferTemplateGreeting } from '../../utils/offerTemplate';

const STATUS_CONFIG = {
  draft: { label: 'Draft', key: 'draft', tone: 'neutral' },
  pending_approval: { label: 'Awaiting approval', key: 'pendingApproval', tone: 'warning' },
  approved: { label: 'Approved', key: 'approved', tone: 'info' },
  sent: { label: 'Sent', key: 'sent', tone: 'info' },
  viewed: { label: 'Viewed', key: 'viewed', tone: 'info' },
  accepted: { label: 'Accepted', key: 'accepted', tone: 'success' },
  declined: { label: 'Declined', key: 'declined', tone: 'danger' }
};

const FILTERS = [
  { id: 'all', label: 'All', key: 'all' },
  { id: 'needs_review', label: 'Awaiting approval', key: 'needsReview' },
  { id: 'draft', label: 'Drafts', key: 'drafts' },
  { id: 'sent', label: 'Sent', key: 'sent' },
  { id: 'viewed', label: 'Viewed', key: 'viewed' },
  { id: 'accepted', label: 'Accepted', key: 'accepted' },
  { id: 'declined', label: 'Declined', key: 'declined' }
];

const DEFAULT_FOLLOW_UP_DAYS = [3, 6];

const DEFAULT_FOLLOW_UP_SUBJECT = 'Reminder: offer for {{job_title}} at {{company_name}}';

const buildDefaultFollowUpMessage = (sequence = 1) => [
  '<p>Hi {{candidate_first_name}},</p>',
  `<p>${sequence === 1 ? 'I wanted to follow up' : 'Just checking in again'} on the offer for {{job_title}} at {{company_name}}.</p>`,
  '<p>Please review the details and let us know if you have any questions.</p>'
].join('');

const ensureOffersHubQuillLoaded = () => new Promise((resolve, reject) => {
  if (window.Quill) {
    resolve();
    return;
  }

  let quillCSS = document.querySelector('link[data-offer-quill="css"]');
  if (!quillCSS) {
    quillCSS = document.createElement('link');
    quillCSS.rel = 'stylesheet';
    quillCSS.href = 'https://cdn.quilljs.com/1.3.6/quill.snow.css';
    quillCSS.setAttribute('data-offer-quill', 'css');
    document.head.appendChild(quillCSS);
  }

  const existingScript = document.querySelector('script[data-offer-quill="js"]');
  if (existingScript && existingScript.getAttribute('data-loaded') === 'true') {
    resolve();
    return;
  }

  let quillScript = existingScript;
  if (!quillScript) {
    quillScript = document.createElement('script');
    quillScript.src = 'https://cdn.quilljs.com/1.3.6/quill.js';
    quillScript.setAttribute('data-offer-quill', 'js');
    document.body.appendChild(quillScript);
  }

  quillScript.addEventListener('load', () => {
    quillScript.setAttribute('data-loaded', 'true');
    resolve();
  });
  quillScript.addEventListener('error', reject);
});

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDateTime = (value) => {
  const millis = toMillis(value);
  if (!millis) return '';
  return new Date(millis).toLocaleString(getUiLocale(), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const compensationLabel = (terms = {}) => {
  if (!terms.baseSalary) return uiText('Not specified');
  return [terms.currency, terms.baseSalary, terms.payFrequency].filter(Boolean).join(' ');
};

const stringifySalaryValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    return value.map(stringifySalaryValue).filter(Boolean).join(' - ');
  }
  if (typeof value === 'object') {
    const directValue = value.listedSalary
      || value.salary
      || value.salaryRange
      || value.compensationRange
      || value.payRange
      || value.range
      || value.amount
      || value.value
      || value.label;
    if (directValue) return stringifySalaryValue(directValue);

    const minValue = value.min ?? value.minimum ?? value.from;
    const maxValue = value.max ?? value.maximum ?? value.to;
    const range = [stringifySalaryValue(minValue), stringifySalaryValue(maxValue)].filter(Boolean).join(' - ');
    const currency = stringifySalaryValue(value.currency || value.currencyCode);
    const frequency = stringifySalaryValue(value.frequency || value.payFrequency || value.period);
    return [currency, range, frequency].filter(Boolean).join(' ').trim();
  }
  return '';
};

const getOfferListedSalary = (offer = {}) => stringifySalaryValue(
  offer.job?.listedSalary
  || offer.job?.salary
  || offer.job?.salaryRange
  || offer.job?.compensationRange
  || offer.job?.payRange
  || offer.job?.compensation
);

const htmlToText = (html = '') => String(html || '')
  .replace(/<br\s*\/?>/gi, '\n')
  .replace(/<\/p>/gi, '\n')
  .replace(/<[^>]*>/g, '')
  .replace(/&nbsp;/g, ' ')
  .replace(/&amp;/g, '&')
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .trim();

const textToHtml = (text = '') => String(text || '')
  .split('\n')
  .map((line) => line.trim())
  .filter(Boolean)
  .map((line) => `<p>${line
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')}</p>`)
  .join('');

const normalizeFollowUpDays = (value) => {
  const source = Array.isArray(value) ? value : String(value || '').split(/[,;\s]+/);
  return [...new Set(source
    .map((item) => {
      if (item && typeof item === 'object') {
        return Number.parseInt(item.daysAfterSend ?? item.day ?? item.days, 10);
      }
      return Number.parseInt(item, 10);
    })
    .filter((day) => Number.isFinite(day) && day > 0)
    .sort((left, right) => left - right))]
    .slice(0, 6);
};

const buildFollowUpSchedule = (value) => {
  const days = normalizeFollowUpDays(value);
  return (days.length ? days : DEFAULT_FOLLOW_UP_DAYS).map((day) => ({
    id: `day_${day}`,
    daysAfterSend: day
  }));
};

const normalizeFollowUpItems = (followUps = {}) => {
  const sourceItems = Array.isArray(followUps.items) && followUps.items.length
    ? followUps.items
    : Array.isArray(followUps.schedule) && followUps.schedule.length
      ? followUps.schedule
      : buildFollowUpSchedule(followUps.cadenceDays || DEFAULT_FOLLOW_UP_DAYS);

  return sourceItems.map((item, index) => {
    const day = Number.parseInt(item.daysAfterSend ?? item.day ?? item.days, 10);
    const safeDay = Number.isFinite(day) && day > 0 ? day : DEFAULT_FOLLOW_UP_DAYS[index] || ((index + 1) * 3);
    return {
      id: item.id || `day_${safeDay}`,
      daysAfterSend: safeDay,
      subject: item.subject || followUps.subject || DEFAULT_FOLLOW_UP_SUBJECT,
      messageHTML: item.messageHTML || item.messageHtml || followUps.messageHTML || followUps.messageHtml || buildDefaultFollowUpMessage(index + 1),
      messageText: htmlToText(item.messageHTML || item.messageHtml || followUps.messageHTML || followUps.messageHtml || buildDefaultFollowUpMessage(index + 1))
    };
  });
};

const createDetailForm = (offer = {}) => {
  const context = getOfferContext(offer);
  const personalize = value => applyOfferPlaceholders(
    getStatus(offer) === 'draft' ? normalizeOfferTemplateGreeting(value) : value,
    context
  );
  const messageHTML = personalize(offer.email?.messageHTML || offer.email?.messageHtml || textToHtml(offer.email?.messageText || ''));
  return {
    subject: applyOfferPlaceholders(offer.email?.subject, context),
    messageHTML,
    messageText: htmlToText(messageHTML),
    followUps: {
      enabled: offer.followUps?.enabled !== false,
      items: normalizeFollowUpItems(offer.followUps || {}).map((item, index) => {
        const messageHTML = applyOfferPlaceholders(personalize(item.messageHTML), { ...context, sequence: String(index + 1) });
        return { ...item, subject: applyOfferPlaceholders(item.subject, context), messageHTML, messageText: htmlToText(messageHTML) };
      })
    },
    terms: {
      baseSalary: offer.terms?.baseSalary || '',
      currency: offer.terms?.currency || 'USD',
      payFrequency: offer.terms?.payFrequency || 'annual',
      bonus: offer.terms?.bonus || '',
      signOnBonus: offer.terms?.signOnBonus || '',
      commission: offer.terms?.commission || '',
      equity: offer.terms?.equity || '',
      employmentType: offer.terms?.employmentType || 'Full-time',
      workMode: offer.terms?.workMode || 'On-site',
      expiresAt: offer.terms?.expiresAt || '',
      startDate: offer.terms?.startDate || ''
    }
  };
};

const getStatus = (offer = {}) => String(offer?.status || 'draft').toLowerCase();

const getCandidateName = (offer = {}) => (
  offer.candidate?.name
  || offer.candidateName
  || offer.entityName
  || 'Candidate'
);

const getOfferContext = (offer = {}) => ({
  candidateName: getCandidateName(offer),
  jobTitle: offer.job?.title || offer.job?.jobTitle || offer.jobTitle || '',
  companyName: offer.companyName || ''
});

const getCandidateId = (offer = {}) => (
  offer.candidate?.id
  || offer.candidate?.applicantId
  || offer.candidate?.candidateId
  || ''
);

const getJobTitle = (offer = {}) => (
  offer.job?.title
  || offer.job?.jobTitle
  || offer.jobTitle
  || 'Role not specified'
);

const getUpdatedAt = (offer = {}) => (
  offer.updatedAt
  || offer.sentAt
  || offer.candidateResponse?.acceptedAt
  || offer.candidateResponse?.declinedAt
  || offer.createdAt
);

const OffersHub = ({
  actionBusyKey = '',
  canDraftOffer = false,
  canSendDraftToCandidate = false,
  canSubmitDraftForApproval = false,
  isOfferApprover = false,
  isOpen,
  loading = false,
  offers = [],
  onApproveOffer,
  onClose,
  onOpenApplicant,
  onRejectOffer,
  onReviewOffer,
  onSaveDraftOffer,
  onSendDraftOffer,
  onSubmitDraftForApproval,
  pendingApprovalByOfferId = new Map()
}) => {
  const { t } = useTranslation('applicants');
  const { isRTL } = useLanguage();
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [selectedOfferId, setSelectedOfferId] = useState('');
  const [detailOfferId, setDetailOfferId] = useState('');
  const [detailForm, setDetailForm] = useState(createDetailForm);
  const [draftActionMode, setDraftActionMode] = useState('');
  const [rejectingOfferId, setRejectingOfferId] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const detailMessageEditorRef = useRef(null);
  const detailMessageQuillRef = useRef(null);
  const detailMessageLoadedOfferIdRef = useRef('');
  const detailFollowUpEditorRefs = useRef({});
  const detailFollowUpQuillRefs = useRef({});
  const detailFollowUpsLoadedOfferIdRef = useRef('');

  const sortedOffers = useMemo(() => {
    return [...offers].sort((left, right) => toMillis(getUpdatedAt(right)) - toMillis(getUpdatedAt(left)));
  }, [offers]);

  const counts = useMemo(() => {
    return sortedOffers.reduce((acc, offer) => {
      const status = getStatus(offer);
      acc.all += 1;
      acc[status] = (acc[status] || 0) + 1;
      if (status === 'pending_approval') acc.needs_review += 1;
      return acc;
    }, { all: 0, needs_review: 0 });
  }, [sortedOffers]);

  const filteredOffers = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return sortedOffers.filter((offer) => {
      const status = getStatus(offer);
      const matchesFilter = filter === 'all'
        || (filter === 'needs_review' && status === 'pending_approval')
        || status === filter;
      if (!matchesFilter) return false;
      if (!normalizedQuery) return true;
      return [
        getCandidateName(offer),
        getJobTitle(offer),
        offer.candidate?.email,
        offer.offerId,
        offer.companyName
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(normalizedQuery));
    });
  }, [filter, query, sortedOffers]);

  const selectedOffer = useMemo(() => {
    return sortedOffers.find((offer) => offer.offerId === selectedOfferId) || null;
  }, [selectedOfferId, sortedOffers]);

  const detailOffer = useMemo(() => {
    return sortedOffers.find((offer) => offer.offerId === detailOfferId) || null;
  }, [detailOfferId, sortedOffers]);
  const canReview = (offer) => isOfferApprover && getStatus(offer) === 'pending_approval';
  const isDetailDraft = Boolean(detailOffer) && getStatus(detailOffer) === 'draft';
  const detailLocked = !isDetailDraft;
  const draftDetailBusy = Boolean(draftActionMode)
    || actionBusyKey === `save:${detailOffer?.offerId}`
    || actionBusyKey === `submit:${detailOffer?.offerId}`
    || actionBusyKey === `send:${detailOffer?.offerId}`;

  useEffect(() => {
    if (isOpen) return;
    // Closing removes the editor nodes; never reuse Quill instances bound to them.
    setDetailOfferId('');
    detailMessageQuillRef.current = null;
    detailMessageLoadedOfferIdRef.current = '';
    detailFollowUpEditorRefs.current = {};
    detailFollowUpQuillRefs.current = {};
    detailFollowUpsLoadedOfferIdRef.current = '';
  }, [isOpen]);

  useEffect(() => {
    if (detailOffer) {
      setDetailForm(createDetailForm(detailOffer));
    } else if (detailOfferId) {
      setDetailOfferId('');
    } else {
      detailMessageLoadedOfferIdRef.current = '';
      detailFollowUpsLoadedOfferIdRef.current = '';
      detailMessageQuillRef.current = null;
      detailFollowUpEditorRefs.current = {};
      detailFollowUpQuillRefs.current = {};
    }
  }, [detailOffer, detailOfferId]);

  useEffect(() => {
    if (!isOpen || !detailOffer || !detailMessageEditorRef.current) return undefined;
    let cancelled = false;
    const initialHTML = createDetailForm(detailOffer).messageHTML;
    const shouldResetContent = detailMessageLoadedOfferIdRef.current !== detailOffer.offerId;

    const setQuillContent = (quill, html) => {
      const delta = quill.clipboard.convert(html || '');
      quill.setContents(delta, 'silent');
    };

    const loadMessageEditor = async () => {
      try {
        await ensureOffersHubQuillLoaded();
        if (cancelled || !detailMessageEditorRef.current) return;

        if (!detailMessageQuillRef.current) {
          const quill = new window.Quill(detailMessageEditorRef.current, {
            theme: 'snow',
            placeholder: t('workspace.offerModal.messagePlaceholder', 'Draft your offer email...'),
            modules: {
              toolbar: [
                ['bold', 'italic', 'underline'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['clean']
              ]
            }
          });
          quill.on('text-change', () => {
            setDetailForm((prev) => ({
              ...prev,
              messageHTML: quill.root.innerHTML,
              messageText: quill.getText().trim()
            }));
          });
          detailMessageQuillRef.current = quill;
        }

        if (shouldResetContent) {
          setQuillContent(detailMessageQuillRef.current, initialHTML);
          detailMessageLoadedOfferIdRef.current = detailOffer.offerId;
        }
        detailMessageQuillRef.current.enable(!detailLocked);
      } catch (error) {
        console.error('Failed to load offer draft editor:', error);
      }
    };

    loadMessageEditor();
    return () => {
      cancelled = true;
    };
  }, [isOpen, detailOffer, detailLocked, t]);

  useEffect(() => {
    if (!isOpen || !detailOffer || !detailForm.followUps.enabled) return undefined;
    let cancelled = false;
    const initialItems = createDetailForm(detailOffer).followUps.items;
    const initialById = new Map(initialItems.map((item) => [item.id, item]));
    const offerChanged = detailFollowUpsLoadedOfferIdRef.current !== detailOffer.offerId;

    const setQuillContent = (quill, html) => {
      const delta = quill.clipboard.convert(html || '');
      quill.setContents(delta, 'silent');
    };

    const loadFollowUpEditors = async () => {
      try {
        await ensureOffersHubQuillLoaded();
        if (cancelled) return;

        detailForm.followUps.items.forEach((item) => {
          const editorNode = detailFollowUpEditorRefs.current[item.id];
          if (!editorNode) return;

          const isNewEditor = !detailFollowUpQuillRefs.current[item.id];
          if (isNewEditor) {
            const quill = new window.Quill(editorNode, {
              theme: 'snow',
              placeholder: t('workspace.offerModal.followUpMessagePlaceholder', 'Draft the reminder email...'),
              modules: {
                toolbar: [
                  ['bold', 'italic', 'underline'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  ['clean']
                ]
              }
            });
            quill.on('text-change', () => {
              updateDetailFollowUpItem(item.id, {
                messageHTML: quill.root.innerHTML,
                messageText: quill.getText().trim()
              });
            });
            detailFollowUpQuillRefs.current[item.id] = quill;
          }

          const initialItem = initialById.get(item.id);
          if (offerChanged && initialItem) {
            setQuillContent(
              detailFollowUpQuillRefs.current[item.id],
              initialItem.messageHTML || textToHtml(initialItem.messageText || '')
            );
          } else if (isNewEditor) {
            setQuillContent(
              detailFollowUpQuillRefs.current[item.id],
              item.messageHTML || textToHtml(item.messageText || '')
            );
          }
          detailFollowUpQuillRefs.current[item.id].enable(!detailLocked);
        });
        detailFollowUpsLoadedOfferIdRef.current = detailOffer.offerId;
      } catch (error) {
        console.error('Failed to load offer draft follow-up editors:', error);
      }
    };

    loadFollowUpEditors();
    return () => {
      cancelled = true;
    };
  }, [isOpen, detailOffer, detailForm.followUps.enabled, detailForm.followUps.items, detailLocked, t]);

  useEffect(() => {
    if (!detailForm.followUps.enabled) {
      detailFollowUpEditorRefs.current = {};
      detailFollowUpQuillRefs.current = {};
    }
  }, [detailForm.followUps.enabled]);

  if (!isOpen) return null;

  const statusLabel = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    return t(`offersHub.status.${config.key}`, config.label);
  };
  const notSetLabel = t('offersHub.notSet', 'Not set');
  const notSpecifiedLabel = t('offersHub.notSpecified', 'Not specified');
  const renderListedSalaryHint = (salary) => salary ? (
    <p className="offers-hub-listed-salary-hint">
      {t('workspace.offerModal.listedSalaryHint', {
        salary,
        defaultValue: 'Listed salary on job post: {{salary}}'
      })}
    </p>
  ) : null;

  const openDetails = (offer) => {
    setSelectedOfferId(offer.offerId);
    setDetailOfferId(offer.offerId);
  };

  const updateDetailTerm = (field, value) => {
    setDetailForm((prev) => ({
      ...prev,
      terms: {
        ...prev.terms,
        [field]: value
      }
    }));
  };

  const updateDetailFollowUp = (field, value) => {
    setDetailForm((prev) => ({
      ...prev,
      followUps: {
        ...prev.followUps,
        [field]: value
      }
    }));
  };

  const updateDetailFollowUpItem = (id, patch) => {
    setDetailForm((prev) => ({
      ...prev,
      followUps: {
        ...prev.followUps,
        items: prev.followUps.items.map((item) => (
          item.id === id ? { ...item, ...patch } : item
        ))
      }
    }));
  };

  const addDetailFollowUpItem = () => {
    setDetailForm((prev) => {
      const items = prev.followUps.items || [];
      const maxDay = items.reduce((max, item) => Math.max(max, Number.parseInt(item.daysAfterSend, 10) || 0), 0);
      const nextDay = maxDay > 0 ? maxDay + 3 : DEFAULT_FOLLOW_UP_DAYS[0];
      const context = getOfferContext(detailOffer);
      const messageHTML = applyOfferPlaceholders(buildDefaultFollowUpMessage(items.length + 1), context);
      return {
        ...prev,
        followUps: {
          ...prev.followUps,
          items: [
            ...items,
            {
              id: `day_${nextDay}_${Date.now()}`,
              daysAfterSend: nextDay,
              subject: applyOfferPlaceholders(DEFAULT_FOLLOW_UP_SUBJECT, context),
              messageHTML,
              messageText: htmlToText(messageHTML)
            }
          ]
        }
      };
    });
  };

  const removeDetailFollowUpItem = (id) => {
    delete detailFollowUpEditorRefs.current[id];
    delete detailFollowUpQuillRefs.current[id];
    setDetailForm((prev) => ({
      ...prev,
      followUps: {
        ...prev.followUps,
        items: prev.followUps.items.length > 1
          ? prev.followUps.items.filter((item) => item.id !== id)
          : prev.followUps.items
      }
    }));
  };

  const buildDraftFromDetailForm = () => {
    const messageHTML = detailMessageQuillRef.current
      ? detailMessageQuillRef.current.root.innerHTML
      : detailForm.messageHTML || textToHtml(detailForm.messageText);
    const messageText = detailMessageQuillRef.current
      ? detailMessageQuillRef.current.getText().trim()
      : detailForm.messageText;

    const followUpItems = detailForm.followUps.items
      .map((item, index) => {
        const day = Number.parseInt(item.daysAfterSend, 10);
        const safeDay = Number.isFinite(day) && day > 0 ? day : DEFAULT_FOLLOW_UP_DAYS[index] || ((index + 1) * 3);
        const editor = detailFollowUpQuillRefs.current[item.id];
        const followUpMessageHTML = editor
          ? editor.root.innerHTML
          : item.messageHTML || textToHtml(item.messageText);
        const followUpMessageText = editor
          ? editor.getText().trim()
          : item.messageText;
        return {
          id: item.id || `day_${safeDay}`,
          daysAfterSend: safeDay,
          subject: item.subject,
          messageText: followUpMessageText,
          messageHTML: followUpMessageHTML
        };
      })
      .sort((left, right) => left.daysAfterSend - right.daysAfterSend);

    return {
      ...detailOffer,
      terms: {
        ...(detailOffer.terms || {}),
        ...detailForm.terms
      },
      email: {
        ...(detailOffer.email || {}),
        subject: detailForm.subject,
        messageHTML,
        messageText
      },
      followUps: {
        ...(detailOffer.followUps || {}),
        enabled: detailForm.followUps.enabled,
        cadenceDays: followUpItems.map((item) => item.daysAfterSend),
        schedule: followUpItems,
        items: followUpItems,
        stopOnCandidateResponse: true
      }
    };
  };

  const validateDraftTimeline = () => {
    const expiresAt = detailForm.terms.expiresAt || '';
    const startDate = detailForm.terms.startDate || '';
    if (expiresAt && startDate && expiresAt > startDate) {
      alert(t('workspace.offerModal.dateOrderError', 'Offer expiration must be on or before the start date.'));
      return false;
    }
    return true;
  };

  const runDraftDetailAction = async (mode) => {
    if (!detailOffer || !isDetailDraft || !canDraftOffer || draftDetailBusy) return;
    if (!validateDraftTimeline()) return;

    setDraftActionMode(mode);
    try {
      const updatedOffer = buildDraftFromDetailForm();
      if (mode === 'submit') {
        await onSubmitDraftForApproval?.(updatedOffer);
      } else if (mode === 'send') {
        await onSendDraftOffer?.(updatedOffer);
      } else {
        await onSaveDraftOffer?.(updatedOffer);
      }
    } finally {
      setDraftActionMode('');
    }
  };

  const handleRejectSubmit = async (offer) => {
    if (!rejectionReason.trim()) return;
    await onRejectOffer?.(offer, rejectionReason.trim());
    setRejectingOfferId('');
    setRejectionReason('');
  };

  return (
    <div
      className="offers-hub-overlay"
      onMouseDown={(event) => event.stopPropagation()}
      onMouseMove={(event) => event.stopPropagation()}
      onMouseUp={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <section className={`offers-hub-modal${isRTL ? ' rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'} role="dialog" aria-modal="true" aria-label={t('offersHub.title', 'Offers')}>
        <header className="offers-hub-header">
          <div>
            <h2>{detailOffer ? getCandidateName(detailOffer) : t('offersHub.title', 'Offers')}</h2>
            <p>{detailOffer ? getJobTitle(detailOffer) : t('offersHub.subtitle', 'Review every draft, approval request, sent offer, and candidate response.')}</p>
          </div>
          <div className="offers-hub-header-actions">
            {detailOffer && (
              <button type="button" className="offers-hub-back" onClick={() => setDetailOfferId('')}>
                <i className={`fa-solid ${isRTL ? 'fa-arrow-right' : 'fa-arrow-left'}`} />
                {t('offersHub.backToAll', 'Back to all offers')}
              </button>
            )}
            <button type="button" className="offers-hub-close" onClick={onClose} aria-label={t('offersHub.close', 'Close offers')}>
              <i className="fa-solid fa-times" />
            </button>
          </div>
        </header>

        {!detailOffer && (
          <div className="offers-hub-toolbar">
            <div className="offers-hub-filters">
              {FILTERS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`offers-hub-filter ${filter === item.id ? 'active' : ''}`}
                  onClick={() => setFilter(item.id)}
                >
                  <span>{t(`offersHub.filters.${item.key}`, item.label)}</span>
                  <strong>{counts[item.id] || 0}</strong>
                </button>
              ))}
            </div>
            <div className="offers-hub-search">
              <i className="fa-solid fa-magnifying-glass" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('offersHub.searchPlaceholder', 'Search offers')}
              />
            </div>
          </div>
        )}

        {!detailOffer ? (
        <div className="offers-hub-content">
          <div className="offers-hub-table-wrap">
            {loading ? (
              <div className="offers-hub-empty">
                <i className="fa-solid fa-spinner fa-spin" />
                <span>{t('offersHub.loading', 'Loading offers')}</span>
              </div>
            ) : filteredOffers.length === 0 ? (
              <div className="offers-hub-empty">
                <i className="fa-solid fa-file-circle-xmark" />
                <span>{t('offersHub.empty', 'No offers match this view')}</span>
              </div>
            ) : (
              <table className="offers-hub-table">
                <thead>
                  <tr>
                    <th>{t('offersHub.table.candidate', 'Candidate')}</th>
                    <th>{t('offersHub.table.role', 'Role')}</th>
                    <th>{t('offersHub.table.status', 'Status')}</th>
                    <th>{t('offersHub.table.compensation', 'Compensation')}</th>
                    <th>{t('offersHub.table.updated', 'Updated')}</th>
                    <th>{t('offersHub.table.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOffers.map((offer) => {
                    const status = getStatus(offer);
                    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
                    const offerId = offer.offerId;
                    const busyApprove = actionBusyKey === `approve:${offerId}`;
                    const busyReject = actionBusyKey === `reject:${offerId}`;
                    return (
                      <tr
                        key={offerId}
                        className={selectedOffer?.offerId === offerId ? 'selected' : ''}
                        onClick={() => setSelectedOfferId(offerId)}
                      >
                        <td>
                          <button
                            type="button"
                            className="offers-hub-link"
                            onClick={(event) => {
                              event.stopPropagation();
                              onOpenApplicant?.(offer);
                            }}
                          >
                            {getCandidateName(offer)}
                          </button>
                          <small>{offer.candidate?.email || t('offersHub.noEmail', 'No email')}</small>
                        </td>
                        <td>
                          <strong>{getJobTitle(offer)}</strong>
                          <small>{offer.job?.department || t('offersHub.noDepartment', 'No department')}</small>
                        </td>
                        <td>
                          <span className={`offers-hub-status ${config.tone}`}>{statusLabel(status)}</span>
                          {pendingApprovalByOfferId.has(offerId) && <small>{t('offersHub.approvalRequestOpen', 'Approval request open')}</small>}
                        </td>
                        <td>{offer.terms?.baseSalary ? compensationLabel(offer.terms) : notSpecifiedLabel}</td>
                        <td>{formatDateTime(getUpdatedAt(offer)) || notSetLabel}</td>
                        <td>
                          <div className="offers-hub-actions">
                            <button type="button" onClick={(event) => {
                              event.stopPropagation();
                              if (canReview(offer)) {
                                onReviewOffer?.(offer);
                              } else {
                                openDetails(offer);
                              }
                            }}>
                              {canReview(offer) ? t('offersHub.review', 'Review') : t('offersHub.details', 'Details')}
                            </button>
                            {canReview(offer) && (
                              <>
                                <button
                                  type="button"
                                  className="primary"
                                  disabled={Boolean(actionBusyKey)}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onApproveOffer?.(offer);
                                  }}
                                >
                                  {busyApprove ? <i className="fa-solid fa-spinner fa-spin" /> : t('offersHub.approveAndSend', 'Approve & send')}
                                </button>
                                <button
                                  type="button"
                                  className="danger"
                                  disabled={Boolean(actionBusyKey)}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setRejectingOfferId(offerId);
                                    setRejectionReason('');
                                  }}
                                >
                                  {busyReject ? <i className="fa-solid fa-spinner fa-spin" /> : t('offersHub.reject', 'Reject')}
                                </button>
                              </>
                            )}
                          </div>
                          {rejectingOfferId === offerId && (
                            <div className="offers-hub-reject" onClick={(event) => event.stopPropagation()}>
                              <textarea
                                value={rejectionReason}
                                onChange={(event) => setRejectionReason(event.target.value)}
                                placeholder={t('offersHub.rejectionReason', 'Rejection reason')}
                              />
                              <div>
                                <button type="button" onClick={() => setRejectingOfferId('')}>{t('offersHub.cancel', 'Cancel')}</button>
                                <button
                                  type="button"
                                  className="danger"
                                  disabled={!rejectionReason.trim() || Boolean(actionBusyKey)}
                                  onClick={() => handleRejectSubmit(offer)}
                                >
                                  {t('offersHub.confirmReject', 'Confirm reject')}
                                </button>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
        ) : (
          <div className="offers-hub-detail-page">
            <section className="offers-hub-detail-section">
              <div className="offers-hub-section-title">
                <i className="fa-solid fa-user" />
                <div>
                  <h3>{t('offersHub.detail.candidateRole', 'Candidate and role')}</h3>
                  <span>{isDetailDraft ? t('offersHub.detail.draftHint', 'Draft fields can be updated before approval.') : t('offersHub.detail.lockedHint', 'Offer details are locked after approval submission.')}</span>
                </div>
              </div>
              <div className="offers-hub-field-grid">
                <div className="offers-hub-field">
                  <label>{t('offersHub.table.candidate', 'Candidate')}</label>
                  <input value={getCandidateName(detailOffer)} disabled />
                </div>
                <div className="offers-hub-field">
                  <label>{t('offersHub.email', 'Email')}</label>
                  <input value={detailOffer.candidate?.email || t('offersHub.noEmail', 'No email')} disabled />
                </div>
                <div className="offers-hub-field">
                  <label>{t('offersHub.table.role', 'Role')}</label>
                  <input value={getJobTitle(detailOffer)} disabled />
                </div>
                <div className="offers-hub-field">
                  <label>{t('offersHub.table.status', 'Status')}</label>
                  <input value={statusLabel(getStatus(detailOffer))} disabled />
                </div>
              </div>
            </section>

            <section className="offers-hub-detail-section">
              <div className="offers-hub-section-title">
                <i className="fa-solid fa-money-check-dollar" />
                <div>
                  <h3>{t('workspace.offerModal.compensationTitle', 'Compensation')}</h3>
                  <span>{t('workspace.offerModal.compensationHint', 'Keep the primary pay terms together.')}</span>
                </div>
              </div>
              <div className="offers-hub-field-grid">
                <div className="offers-hub-field">
                  <label>{t('workspace.offerModal.baseSalary', 'Base salary')}</label>
                  <input value={detailForm.terms.baseSalary} disabled={detailLocked} onChange={(event) => updateDetailTerm('baseSalary', event.target.value)} />
                  {renderListedSalaryHint(getOfferListedSalary(detailOffer))}
                </div>
                <div className="offers-hub-field">
                  <label>{t('workspace.offerModal.currency', 'Currency')}</label>
                  <select value={detailForm.terms.currency} disabled={detailLocked} onChange={(event) => updateDetailTerm('currency', event.target.value)}>
                    <option value="USD">USD</option>
                    <option value="CAD">CAD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="AED">AED</option>
                    <option value="SAR">SAR</option>
                  </select>
                </div>
                <div className="offers-hub-field">
                  <label>{t('workspace.offerModal.payFrequency', 'Pay frequency')}</label>
                  <select value={detailForm.terms.payFrequency} disabled={detailLocked} onChange={(event) => updateDetailTerm('payFrequency', event.target.value)}>
                    <option value="annual">{t('workspace.offerModal.annual', 'Annual')}</option>
                    <option value="monthly">{t('workspace.offerModal.monthly', 'Monthly')}</option>
                    <option value="hourly">{t('workspace.offerModal.hourly', 'Hourly')}</option>
                  </select>
                </div>
                <div className="offers-hub-field">
                  <label>{t('workspace.offerModal.bonus', 'Bonus')}</label>
                  <input value={detailForm.terms.bonus} disabled={detailLocked} onChange={(event) => updateDetailTerm('bonus', event.target.value)} />
                </div>
                <div className="offers-hub-field">
                  <label>{t('workspace.offerModal.signOnBonus', 'Sign-on bonus')}</label>
                  <input value={detailForm.terms.signOnBonus} disabled={detailLocked} onChange={(event) => updateDetailTerm('signOnBonus', event.target.value)} />
                </div>
                <div className="offers-hub-field">
                  <label>{t('workspace.offerModal.commission', 'Commission')}</label>
                  <input value={detailForm.terms.commission} disabled={detailLocked} onChange={(event) => updateDetailTerm('commission', event.target.value)} />
                </div>
                <div className="offers-hub-field offers-hub-field-full">
                  <label>{t('workspace.offerModal.equity', 'Equity')}</label>
                  <input value={detailForm.terms.equity} disabled={detailLocked} onChange={(event) => updateDetailTerm('equity', event.target.value)} />
                </div>
              </div>
            </section>

            <section className="offers-hub-detail-section">
              <div className="offers-hub-section-title">
                <i className="fa-solid fa-briefcase" />
                <div>
                  <h3>{t('workspace.offerModal.workSetupTitle', 'Work setup')}</h3>
                  <span>{t('workspace.offerModal.workSetupHint', 'Employment and location expectations.')}</span>
                </div>
              </div>
              <div className="offers-hub-field-grid">
                <div className="offers-hub-field">
                  <label>{t('workspace.offerModal.employmentType', 'Employment type')}</label>
                  <select value={detailForm.terms.employmentType} disabled={detailLocked} onChange={(event) => updateDetailTerm('employmentType', event.target.value)}>
                    <option value="Full-time">{t('workspace.offerModal.fullTime', 'Full-time')}</option>
                    <option value="Part-time">{t('workspace.offerModal.partTime', 'Part-time')}</option>
                    <option value="Contract">{t('workspace.offerModal.contract', 'Contract')}</option>
                    <option value="Internship">{t('workspace.offerModal.internship', 'Internship')}</option>
                  </select>
                </div>
                <div className="offers-hub-field">
                  <label>{t('workspace.offerModal.workMode', 'Work mode')}</label>
                  <select value={detailForm.terms.workMode} disabled={detailLocked} onChange={(event) => updateDetailTerm('workMode', event.target.value)}>
                    <option value="On-site">{t('workspace.offerModal.onSite', 'On-site')}</option>
                    <option value="Hybrid">{t('workspace.offerModal.hybrid', 'Hybrid')}</option>
                    <option value="Remote">{t('workspace.offerModal.remote', 'Remote')}</option>
                  </select>
                </div>
              </div>
            </section>

            <section className="offers-hub-detail-section">
              <div className="offers-hub-section-title">
                <i className="fa-solid fa-calendar-check" />
                <div>
                  <h3>{t('workspace.offerModal.timelineTitle', 'Offer timeline')}</h3>
                  <span>{t('workspace.offerModal.timelineHint', 'Candidate response comes before the first day.')}</span>
                </div>
              </div>
              <div className="offers-hub-field-grid">
                <div className="offers-hub-field">
                  <label>{t('workspace.offerModal.expiresAt', 'Offer expires')}</label>
                  <input type="date" value={detailForm.terms.expiresAt} disabled={detailLocked} onChange={(event) => updateDetailTerm('expiresAt', event.target.value)} />
                </div>
                <div className="offers-hub-field">
                  <label>{t('workspace.offerModal.startDate', 'Start date')}</label>
                  <input type="date" value={detailForm.terms.startDate} disabled={detailLocked} onChange={(event) => updateDetailTerm('startDate', event.target.value)} />
                </div>
                <div className="offers-hub-field">
                  <label>{t('offersHub.created', 'Created')}</label>
                  <input value={formatDateTime(detailOffer.createdAt) || notSetLabel} disabled />
                </div>
                <div className="offers-hub-field">
                  <label>{t('offersHub.table.updated', 'Updated')}</label>
                  <input value={formatDateTime(getUpdatedAt(detailOffer)) || notSetLabel} disabled />
                </div>
              </div>
            </section>

            <section className="offers-hub-detail-section">
              <div className="offers-hub-section-title">
                <i className="fa-solid fa-envelope" />
                <div>
                  <h3>{t('offersHub.detail.offerMessage', 'Offer message')}</h3>
                  <span>{t('offersHub.detail.offerMessageHint', 'Draft email content and candidate-facing subject.')}</span>
                </div>
              </div>
              <div className="offers-hub-field-grid">
                <div className="offers-hub-field offers-hub-field-full">
                  <label>{t('workspace.offerModal.subject', 'Subject')}</label>
                  <input value={detailForm.subject} disabled={detailLocked} onChange={(event) => setDetailForm((prev) => ({ ...prev, subject: event.target.value }))} />
                </div>
                <div className="offers-hub-field offers-hub-field-full offers-hub-message-field">
                  <label>{t('workspace.offerModal.message', 'Message')}</label>
                  <div className={`offers-hub-quill-frame offers-hub-message-editor ${detailLocked ? 'locked' : ''}`}>
                    <div ref={detailMessageEditorRef} className="offers-hub-quill-root" />
                  </div>
                </div>
              </div>
            </section>

            <section className="offers-hub-detail-section">
              <div className="offers-hub-section-title">
                <i className="fa-solid fa-bell" />
                <div>
                  <h3>{t('workspace.offerModal.followUpsTitle', 'Reminder emails')}</h3>
                  <span>{t('workspace.offerModal.followUpsHint', 'Follow-ups are sent only while the offer is awaiting a candidate response.')}</span>
                </div>
              </div>
              <label className="offers-hub-checkbox-row">
                <input
                  type="checkbox"
                  checked={detailForm.followUps.enabled}
                  disabled={detailLocked}
                  onChange={(event) => updateDetailFollowUp('enabled', event.target.checked)}
                />
                <span>{t('workspace.offerModal.followUps', 'Send automated follow-ups until the candidate responds')}</span>
              </label>
              {detailForm.followUps.enabled && (
                <div className="offers-hub-followup-list">
                  {detailForm.followUps.items.map((item, index) => (
                    <div className="offers-hub-followup-item" key={item.id}>
                      <div className="offers-hub-followup-top">
                        <div className="offers-hub-field">
                          <label>{t('workspace.offerModal.followUpSendAfter', 'Send after')}</label>
                          <input
                            type="number"
                            min="1"
                            value={item.daysAfterSend}
                            disabled={detailLocked}
                            onChange={(event) => updateDetailFollowUpItem(item.id, {
                              daysAfterSend: Math.max(1, Number.parseInt(event.target.value, 10) || 1)
                            })}
                          />
                        </div>
                        <button type="button" disabled={detailLocked || detailForm.followUps.items.length <= 1} onClick={() => removeDetailFollowUpItem(item.id)}>
                          {t('workspace.offerModal.remove', 'Remove')}
                        </button>
                      </div>
                      <div className="offers-hub-field">
                        <label>{t('workspace.offerModal.followUpSubjectNumbered', 'Reminder {{number}} subject', { number: index + 1 })}</label>
                        <input value={item.subject} disabled={detailLocked} onChange={(event) => updateDetailFollowUpItem(item.id, { subject: event.target.value })} />
                      </div>
                      <div className="offers-hub-field">
                        <label>{t('workspace.offerModal.followUpMessageNumbered', 'Reminder {{number}} email', { number: index + 1 })}</label>
                        <div className={`offers-hub-quill-frame offers-hub-followup-editor ${detailLocked ? 'locked' : ''}`}>
                          <div
                            ref={(node) => {
                              if (node) {
                                detailFollowUpEditorRefs.current[item.id] = node;
                              }
                            }}
                            className="offers-hub-quill-root"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {!detailLocked && (
                    <button type="button" className="offers-hub-primary-wide" onClick={addDetailFollowUpItem}>
                      <i className="fa-solid fa-plus" />
                      {t('workspace.offerModal.addReminder', 'Add reminder')}
                    </button>
                  )}
                </div>
              )}
            </section>

            <div className="offers-hub-detail-footer">
              {getCandidateId(detailOffer) && (
                <button type="button" className="offers-hub-primary-wide" onClick={() => onOpenApplicant?.(detailOffer)}>
                  {t('offersHub.openCandidate', 'Open candidate')}
                </button>
              )}
              {isDetailDraft && canDraftOffer && (
                <button type="button" className="offers-hub-primary-wide secondary" onClick={() => runDraftDetailAction('save')} disabled={draftDetailBusy}>
                  {draftActionMode === 'save' ? <i className="fa-solid fa-spinner fa-spin" /> : t('offersHub.saveDraft', 'Save draft')}
                </button>
              )}
              {isDetailDraft && canDraftOffer && canSubmitDraftForApproval && (
                <button type="button" className="offers-hub-primary-wide" onClick={() => runDraftDetailAction('submit')} disabled={draftDetailBusy}>
                  {draftActionMode === 'submit' ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-file-contract" />}
                  {t('offersHub.pushForApproval', 'Push for approval')}
                </button>
              )}
              {isDetailDraft && canDraftOffer && canSendDraftToCandidate && (
                <button type="button" className="offers-hub-primary-wide" onClick={() => runDraftDetailAction('send')} disabled={draftDetailBusy}>
                  {draftActionMode === 'send' ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-paper-plane" />}
                  {t('offersHub.sendToCandidate', 'Send to candidate')}
                </button>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default OffersHub;
